import { gridDateComparator, gridNumberComparator } from '@mui/x-data-grid-pro';
import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import { DateFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SupplierRelationshipType } from '@/generated/gql/graphql';
import { useConfig } from '~/components/ConfigProvider';
import {
  cellFactory,
  CuriosityAutocompleteCell,
  DataGridConfig,
  DateEditCell,
  dateFormatterFactory,
  DeleteActionCell,
  editCellFactory,
  gridColDefFactory,
  gridColumnGroupFactory,
  headerGroupWithIcon,
  SupplierEditCell,
  UseDataEditGridConfig,
} from '~/components/DataGridCustom';
import { CuriosityLinkCell } from '~/components/DataGridCustom/cells/CuriosityLinkCell/CuriosityLinkCell';
import { CurrencyCell } from '~/components/DataGridCustom/editCells/CurrencyCell/CurrencyCell';
import { DecimalTextFieldCell } from '~/components/DataGridCustom/editCells/DecimalTextFieldCell/DecimalTextFieldCell';
import { TextAreaCell } from '~/components/DataGridCustom/editCells/TextAreaCell/TextAreaCell';
import { TextFieldCell } from '~/components/DataGridCustom/editCells/TextFieldCell/TextFieldCell';
import { IconId } from '~/components/Icon';
import { useModuleConfig } from '~/components/ModuleConfigProvider';
import { RouteId } from '~/routes';

import { DemandRequestWidgetConfig } from './DemandRequestWidget.config';
import {
  DemandRequestLineItemGridColumnGroup,
  DemandRequestLineItemGridField,
  DemandRequestLineItemGridRow,
} from './DemandRequestWidget.types';

const relationshipColorMap: { [key in SupplierRelationshipType]?: string } = {
  [SupplierRelationshipType.known]: 'success.main',
  [SupplierRelationshipType.partner]: 'secondary.main',
};
export const statusColorFromRelationship = (value: SupplierRelationshipType): string | undefined => {
  return value ? relationshipColorMap[value] : undefined;
};

const pathPrefix = ['demandRequest', 'lineItems'];

export const useDemandRequestGridConfig: UseDataEditGridConfig<DemandRequestLineItemGridRow> = ({
  getRowIndex,
  onRowDelete,
}) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const { t: tColumn } = useTranslation(undefined, { keyPrefix: 'DemandRequestLineItemGrid.column' });
  const { t: tGroup } = useTranslation(undefined, { keyPrefix: 'DemandRequestLineItemGrid.group' });
  const widgetConfig = useModuleConfig<DemandRequestWidgetConfig>();
  const { features } = useConfig();

  return useMemo(() => {
    const standardCellConfig = {
      pathPrefix,
      getRowIndex,
      languageNamespace: undefined,
      keyPrefix: 'DemandRequestLineItemGrid.column',
    };

    // Allow editing as admin for easier testing
    const commodityRenderCell = hasPermission(Permission.AdminMaintenance)
      ? editCellFactory(CuriosityAutocompleteCell, {
          ...standardCellConfig,
          ns: CuriosityNamespaceType.COMMODITY,
        })
      : cellFactory(CuriosityLinkCell, ({ row }) => ({
          to: RouteId.commodityDetail(row.commodityId),
          ns: CuriosityNamespaceType.COMMODITY,
          needs: {
            permission: Permission.BaseCommodityList,
          },
        }));

    const createColumn = gridColDefFactory({ tColumn, t, defaultOptions: { sortable: false } });
    const createGroup = gridColumnGroupFactory({ tGroup });

    return {
      sortModel: [],
      columnVisibilityModel: {
        [DemandRequestLineItemGridField.PREFERRED_SUPPLIER_ID]: !widgetConfig.lineItems?.preferredSupplierId?.hidden,
        [DemandRequestLineItemGridField.ORGANIZATION]: features.showOrganization,
        [DemandRequestLineItemGridField.DELIVERY_LOCATION]: features.showDeliveryLocation,
      },
      columns: [
        createColumn({
          field: DemandRequestLineItemGridField.POSITION,
          minWidth: 100,
          hideable: false,
          resizable: false,
          renderCell: editCellFactory(TextFieldCell, standardCellConfig),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.TITLE,
          minWidth: 300,
          renderCell: editCellFactory(TextFieldCell, standardCellConfig),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.DESCRIPTION,
          minWidth: 300,
          renderCell: editCellFactory(TextAreaCell, standardCellConfig),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.QUANTITY,
          minWidth: 150,
          sortComparator: gridNumberComparator,
          valueGetter: (_value, row) => {
            return row.quantity;
          },
          renderCell: editCellFactory(DecimalTextFieldCell, {
            ...standardCellConfig,
            decimalScale: 2,
          }),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.UNIT,
          minWidth: 150,
          renderCell: editCellFactory(CuriosityAutocompleteCell, {
            ...standardCellConfig,
            ns: CuriosityNamespaceType.UNIT,
          }),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.EXPECTED_PRICE_PER_UNIT,
          minWidth: 150,
          sortComparator: gridNumberComparator,
          valueGetter: (_value, row) => {
            return row.expectedPricePerUnit;
          },
          renderCell: editCellFactory(CurrencyCell, {
            ...standardCellConfig,
            decimalScale: 2,
          }),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.BASELINE_PRICE_PER_UNIT,
          minWidth: 150,
          sortComparator: gridNumberComparator,
          renderCell: editCellFactory(CurrencyCell, {
            ...standardCellConfig,
            decimalScale: 2,
          }),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.COMMODITY,
          minWidth: 150,
          renderCell: commodityRenderCell,
        }),
        createColumn({
          field: DemandRequestLineItemGridField.COMMODITY_GROUP_ID,
          minWidth: 150,
          renderCell: editCellFactory(CuriosityAutocompleteCell, {
            ...standardCellConfig,
            ns: CuriosityNamespaceType.COMMODITY_GROUP,
          }),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.PREFERRED_SUPPLIER_ID,
          minWidth: 300,
          renderCell: editCellFactory(SupplierEditCell, standardCellConfig),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.ORGANIZATION,
          minWidth: 300,
          renderCell: editCellFactory(CuriosityAutocompleteCell, {
            ...standardCellConfig,
            ns: CuriosityNamespaceType.ORGANIZATION,
          }),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.FROM,
          valueGetter: (_value, row) => row.shipmentTerms?.earlyDeliveryAt,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          sortComparator: gridDateComparator,
          minWidth: 200,
          renderCell: editCellFactory(DateEditCell, standardCellConfig),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.TO,
          valueGetter: (_value, row) => row.shipmentTerms?.lateDeliveryAt,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          sortComparator: gridDateComparator,
          minWidth: 200,
          renderCell: editCellFactory(DateEditCell, standardCellConfig),
        }),
        createColumn({
          field: DemandRequestLineItemGridField.DELETE_ACTION,
          width: 60,
          renderHeader: () => null,
          align: 'center',
          renderCell: editCellFactory(DeleteActionCell, {
            pathPrefix,
            onClick: onRowDelete,
          }),
        }),
      ],
      columnGroupingModel: [
        createGroup({
          groupId: DemandRequestLineItemGridColumnGroup.REQUEST_ITEM,
          renderHeaderGroup: headerGroupWithIcon(IconId.demandRequestItem),
          children: [
            //
            { field: DemandRequestLineItemGridField.POSITION },
            { field: DemandRequestLineItemGridField.TITLE },
            { field: DemandRequestLineItemGridField.COMMODITY_GROUP_ID },
            { field: DemandRequestLineItemGridField.DESCRIPTION },
            { field: DemandRequestLineItemGridField.UNIT },
            { field: DemandRequestLineItemGridField.QUANTITY },
            { field: DemandRequestLineItemGridField.EXPECTED_PRICE_PER_UNIT },
            { field: DemandRequestLineItemGridField.PREFERRED_SUPPLIER_ID },
            { field: DemandRequestLineItemGridField.ORGANIZATION },
          ],
        }),
        createGroup({
          groupId: DemandRequestLineItemGridColumnGroup.DELIVERY_DATE,
          renderHeaderGroup: headerGroupWithIcon(IconId.deliveryDate),
          children: [
            //
            { field: DemandRequestLineItemGridField.FROM },
            { field: DemandRequestLineItemGridField.TO },
          ],
        }),
        createGroup({
          groupId: DemandRequestLineItemGridColumnGroup.ACTIONS,
          headerName: '',
          children: [
            //
            { field: DemandRequestLineItemGridField.DELETE_ACTION },
          ],
        }),
      ],
    } as DataGridConfig<DemandRequestLineItemGridRow>;
  }, [
    getRowIndex,
    onRowDelete,
    t,
    tColumn,
    tGroup,
    widgetConfig,
    features.showOrganization,
    features.showDeliveryLocation,
    hasPermission,
  ]);
};
